import { readonly, computed } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

interface ChartStyleButtonBackgroundColorOption {
  title: string;
  hexCode: string;
  isDarkColor: boolean;
};

interface ChartStyleButtonIndexOption {
  title: string;
  value: number;
};

const MAX_NUMBER_OF_CHART_STYLE_BUTTONS = 12;
const CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS = readonly<ChartStyleButtonBackgroundColorOption[]>([
  {
    title: 'Black',
    hexCode: '#16191d',
    isDarkColor: true
  },
  {
    title: 'Dark Grey',
    hexCode: '#707f8f',
    isDarkColor: true
  },
  {
    title: 'Dark Blue',
    hexCode: '#0e263e',
    isDarkColor: true
  },
  {
    title: 'Blue',
    hexCode: '#0f4d8a',
    isDarkColor: true
  },
  {
    title: 'Light Blue',
    hexCode: '#3281d0',
    isDarkColor: true
  },
  {
    title: 'Green',
    hexCode: '#219155',
    isDarkColor: true
  },
  {
    title: 'Light Green',
    hexCode: '#2abb67',
    isDarkColor: false
  },
  {
    title: 'Purple',
    hexCode: '#8740a5',
    isDarkColor: true
  },
  {
    title: 'Orange',
    hexCode: '#f9690e',
    isDarkColor: true
  },
  {
    title: 'Red',
    hexCode: '#cc000e',
    isDarkColor: true
  },
  {
    title: 'Yellow',
    hexCode: '#f8d23a',
    isDarkColor: false
  },
  {
    title: 'Pink',
    hexCode: '#e600ac',
    isDarkColor: true
  }
]);
const DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION = CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS[1];
const CHART_STYLE_BUTTON_INDEX_OPTIONS = computed<ChartStyleButtonIndexOption[]>(() => {
  const chartStyleButtonIndexOptions = [
    ...Array(MAX_NUMBER_OF_CHART_STYLE_BUTTONS).keys()
  ].map((element, index) => {
    return {
      title: `#${index + 1}`,
      value: index
    };
  });

  return chartStyleButtonIndexOptions;
});

const MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS = 2;

async function addChartStyleButton(chartStyleRecid: number, chartStyleButtonIndex: number, chartStyleButtonBackgroundColorHexCode: string, chartStyleButtonLabel: string): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to add ChartStyle Button - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  const locationNumber = (chartStyleButtonIndex + 1).toString();
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'add-stylebutton',
        chartCode: chartCodeCopy,
        styleid: chartStyleRecid.toString(),
        location: locationNumber,
        color: chartStyleButtonBackgroundColorHexCode,
        label: chartStyleButtonLabel
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('Add ChartStyle Button jsonLayerResponse.', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to add ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel}.`, e);

    throw new Error(`Failed to add ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to add ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to add ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function removeChartStyleButton(chartStyleRecid: number): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to remove ChartStyle Button - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'delete-stylebutton',
        chartCode: chartCodeCopy,
        styleid: chartStyleRecid.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('Remove ChartStyle Button jsonLayerResponse.', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to remove ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid}.`, e);

    throw new Error(`Failed to remove ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to remove ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to remove ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function moveChartStyleButton(chartStyleRecid: number, chartStyleButtonIndex: number): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to move ChartStyle Button - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  const locationNumber = (chartStyleButtonIndex + 1).toString();
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'move-stylebutton',
        chartCode: chartCodeCopy,
        styleid: chartStyleRecid.toString(),
        location: locationNumber
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('Move ChartStyle Button jsonLayerResponse.', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to move ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber}.`, e);

    throw new Error(`Failed to move ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to move ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to move ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function updateChartStyleButtonChartStyle(newChartStyleRecid: number, oldChartStyleRecid: number): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to update ChartStyle Button ChartStyle - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'change-stylebutton-style',
        chartCode: chartCodeCopy,
        'new-styleid': newChartStyleRecid.toString(),
        styleid: oldChartStyleRecid.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('Update ChartStyle Button ChartStyle jsonLayerResponse.', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update ChartStyle button ChartStyle for chartCode: ${chartCodeCopy} and newChartStyleRecid: ${newChartStyleRecid} and oldChartStyleRecid: ${oldChartStyleRecid}.`, e);

    throw new Error(`Failed to update ChartStyle button ChartStyle for chartCode: ${chartCodeCopy} and newChartStyleRecid: ${newChartStyleRecid} and oldChartStyleRecid: ${oldChartStyleRecid}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update ChartStyle button ChartStyle for chartCode: ${chartCodeCopy} and newChartStyleRecid: ${newChartStyleRecid} and oldChartStyleRecid: ${oldChartStyleRecid} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update ChartStyle button ChartStyle for chartCode: ${chartCodeCopy} and newChartStyleRecid: ${newChartStyleRecid} and oldChartStyleRecid: ${oldChartStyleRecid} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function updateChartStyleButton(chartStyleRecid: number, chartStyleButtonIndex: number, chartStyleButtonBackgroundColorHexCode: string, chartStyleButtonLabel: string): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to update ChartStyle Button - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  const locationNumber = (chartStyleButtonIndex + 1).toString();
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'update-stylebutton',
        chartCode: chartCodeCopy,
        styleid: chartStyleRecid.toString(),
        location: locationNumber,
        color: chartStyleButtonBackgroundColorHexCode,
        label: chartStyleButtonLabel
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('Update ChartStyle Button jsonLayerResponse.', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel}.`, e);

    throw new Error(`Failed to update ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update ChartStyle button for chartCode: ${chartCodeCopy} and recid: ${chartStyleRecid} and location: ${locationNumber} and color: ${chartStyleButtonBackgroundColorHexCode} and label: ${chartStyleButtonLabel} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

export function useChartStyleButtons() {
  return {
    MAX_NUMBER_OF_CHART_STYLE_BUTTONS,
    CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS,
    DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION,
    CHART_STYLE_BUTTON_INDEX_OPTIONS,
    MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS,
    addChartStyleButton,
    removeChartStyleButton,
    moveChartStyleButton,
    updateChartStyleButtonChartStyle,
    updateChartStyleButton
  };
}
